import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BigNumber from "bignumber.js";
import { useProjections } from "../hooks/useProjections";
import { useAuth } from "../hooks/useAuth";
import { buildProjectionRows } from "../utils/projectionRows";
import { ProjectionTable } from "../components/ProjectionTable";
import { IncomeAnalysis } from "../components/IncomeAnalysis";
import { AssumptionsDisplay } from "../components/AssumptionsDisplay";

type ProjectionTab = "table" | "income" | "assumptions";

const TABS: { id: ProjectionTab; label: string }[] = [
  { id: "table", label: "📊 Table" },
  { id: "income", label: "💰 Income" },
  { id: "assumptions", label: "⚙️ Assumptions" },
];

function ProjectionsWeb() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, result, assetBreakdown, loading, error } = useProjections(
    user?.userId
  );

  const [activeTab, setActiveTab] = useState<ProjectionTab>("table");

  const rows = useMemo(
    () => (data ? buildProjectionRows(data, assetBreakdown ?? undefined) : []),
    [data, assetBreakdown]
  );

  const sustainableMonthlyIncome = useMemo(
    () =>
      data && result
        ? new BigNumber(result.finalAmount)
            .times(data.annualReturn)
            .div(12)
            .toNumber()
        : 0,
    [result, data]
  );

  const retirementMonthlyExpenses = useMemo(
    () =>
      data
        ? new BigNumber(data.monthlyExpenses)
            .times(1 + data.expensesAtRetirement)
            .toNumber()
        : 0,
    [data]
  );

  const retirementAge = data ? Math.round(data.retirementAge) : 0;
  const currentYear = new Date().getFullYear();
  const retirementYear = data
    ? currentYear + Math.round(data.retirementAge - data.currentAge)
    : currentYear;

  function handleEditSettings(category: string) {
    navigate("/settings", {
      state: { category, returnTo: "/projections" },
    });
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        Loading projections…
      </div>
    );
  }

  if (error || !data || !result) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        Unable to load projections.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate("/dashboard")}
          aria-label="Back to dashboard"
          className="text-text-muted hover:text-text-primary text-sm"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold">Projection Details</h1>
      </div>

      <div role="tablist" className="mb-6 flex gap-2 border-b border-gray-700">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === tab.id
                ? "border-b-2 border-blue-400 text-blue-400"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "table" && (
        <ProjectionTable rows={rows} currency={data.currency} />
      )}

      {activeTab === "income" && (
        <IncomeAnalysis
          cppAnnual={0}
          oasAnnual={0}
          investmentIncomeAnnual={Math.round(sustainableMonthlyIncome * 12)}
          monthlyExpenses={retirementMonthlyExpenses}
          retirementYear={retirementYear}
          retirementAge={retirementAge}
          currency={data.currency}
          onAdjustExpenses={() => handleEditSettings("expenses")}
        />
      )}

      {activeTab === "assumptions" && (
        <AssumptionsDisplay
          expectedReturnRate={data.annualReturn}
          inflationRate={data.inflationRate}
          realEstateAppreciationRate={
            assetBreakdown?.realEstateAppreciationRate ?? 0.035
          }
          expenseGrowthRate={Math.abs(data.expensesAtRetirement)}
          onEdit={(category) => handleEditSettings(category)}
        />
      )}
    </div>
  );
}

export default ProjectionsWeb;
